import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum-slim';
import {
  accountId,
  backendVersion,
  baseUri,
  datadogEnvironment,
  datadogRUMApplicationId,
  datadogRUMClientToken,
  datadogSite,
  frontendVersion,
  userId,
} from '@gonfalon/constants/constants';
import { getDogfoodClient } from '@gonfalon/dogfood-client/getDogfoodClient';
import {
  datadogRUMPremiumSampleRate,
  datadogRUMSampleRate,
  enableDataDogRumAllowedTracingUrls,
  enableDataDogRumInteractions,
  enableDataDogRumResourceTracking,
  enableFullStoryLinksInDataDog,
  enableManuallyTrackedDdrumViews,
  frontendDatadogFlagRumTracking,
  isDatadogRUMAppEnabled,
  shouldTrackWebVitals,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { LDObserve } from '@launchdarkly/observability';
import type {
  CLSMetricWithAttribution,
  FIDMetricWithAttribution,
  INPMetricWithAttribution,
  LCPMetricWithAttribution,
} from 'web-vitals/attribution';
import { onCLS, onFID, onINP, onLCP } from 'web-vitals/attribution';

import { getSessionUrlWithTimestamp } from './internal/getSessionUrl';
import { isBotUserAgent } from './internal/isBotUserAgent';
import { mergeLDContextKeyAttributes } from './internal/ldContextKeyAttributes';
import {
  initializeNewRelicRum,
  nrAddPageAction,
  nrSetApplicationVersion,
  nrSetCustomAttribute,
  nrSetUserId,
} from './internal/newRelicRum';
import { trackDogfoodError } from './internal/trackDogfoodError';
import { DEFAULT_OWNER } from './setRouteContext';
import { trackError } from './trackError';
declare global {
  interface Window {
    __bundle__: string;
    _DATADOG_SYNTHETICS_BROWSER?: boolean;
    // Used to detect E2E test environments
    __PLAYWRIGHT_TEST__?: boolean;
    FS: {
      getCurrentSessionURL(now?: boolean): string;
      setVars(scope: string, vars: { [key: string]: unknown }): void;
    };
  }
}

// Detect if running in an E2E test environment (Playwright)
export const isE2ETest = () => window.__PLAYWRIGHT_TEST__ === true;

function initializeDatadogRum() {
  if (!isDatadogRUMAppEnabled() || isBotUserAgent(navigator.userAgent)) {
    return;
  }

  datadogRum.init({
    applicationId: datadogRUMApplicationId(),
    clientToken: datadogRUMClientToken(),
    sessionSampleRate: datadogRUMSampleRate(),
    sessionReplaySampleRate: datadogRUMPremiumSampleRate(),
    env: datadogEnvironment(),
    version: frontendVersion(),
    site: datadogSite() as RumInitConfiguration['site'], // `site` is a union type of valid datadog sites; hard to validate here, so we're just assuming it's valid
    trackUserInteractions: enableDataDogRumInteractions(),
    trackResources: enableDataDogRumResourceTracking(),
    service: 'gonfalon',
    trackViewsManually: enableManuallyTrackedDdrumViews(),
    enableExperimentalFeatures: frontendDatadogFlagRumTracking() ? ['feature_flags'] : undefined,
    allowedTracingUrls: enableDataDogRumAllowedTracingUrls() ? [baseUri()] : undefined,
    beforeSend: (event, context) => {
      if (
        event.type === 'error' &&
        window.FS !== undefined &&
        typeof window.FS.getCurrentSessionURL === 'function' &&
        enableFullStoryLinksInDataDog()
      ) {
        // oxlint-disable-next-line no-param-reassign
        event.context = { ...event.context, FS_url_at_exception: window.FS.getCurrentSessionURL(true) };
      }
      const sessionURL = getSessionUrlWithTimestamp();
      if (sessionURL) {
        // oxlint-disable-next-line no-param-reassign
        event.context = { ...event.context, sessionURL };
      }
      if (event.type === 'resource') {
        let resourceData: {
          encodedBodySize?: PerformanceResourceTiming['encodedBodySize'];
          transferSize?: PerformanceResourceTiming['transferSize'];
          renderBlockingStatus?: string;
          cacheMode?: 'local' | 'validated';
          name?: string;
        } = {};
        try {
          if ('performanceEntry' in context) {
            if (context.performanceEntry instanceof PerformanceResourceTiming) {
              const { transferSize, encodedBodySize } = context.performanceEntry;
              const renderBlockingStatus =
                'renderBlockingStatus' in context.performanceEntry
                  ? (context.performanceEntry.renderBlockingStatus as string)
                  : undefined;
              // this copies the cache mode logic in https://www.w3.org/TR/resource-timing/#dfn-cache-mode
              let cacheMode: 'local' | 'validated' | undefined;
              if (transferSize === 0) {
                cacheMode = 'local';
              } else if (transferSize === 300) {
                cacheMode = 'validated';
              }
              resourceData = { cacheMode, transferSize, encodedBodySize, renderBlockingStatus };
            }
          }
          if (['css', 'js'].some((type) => type === event.resource.type)) {
            const regex =
              process.env.NODE_ENV === 'production'
                ? /\/s\/ld\/([^/]+?)\.[0-9a-fA-F]+\.(js|css)$/
                : /\/s\/ld\/([^/]+\.(css|js))$/;
            const match = event.resource.url.match(regex);

            if (match) {
              const endOfPath = process.env.NODE_ENV === 'production' ? `${match[1]}.${match[2]}` : match[1];
              resourceData.name = endOfPath;
            }
          }
        } catch {
          //noop
        }
        if (Object.keys(resourceData).length > 0) {
          // oxlint-disable-next-line no-param-reassign
          event.context = { ...event.context, resource: resourceData };
        }
      }
      return true;
    },
  });

  datadogRum.setGlobalContextProperty('entrypoint', window.__bundle__);
  datadogRum.setGlobalContextProperty('owner', DEFAULT_OWNER);
  datadogRum.setGlobalContextProperty('account', { id: accountId() });
  datadogRum.setGlobalContextProperty('user', { id: userId() });
  datadogRum.setGlobalContextProperty('backendVersion', backendVersion());
  if (enableManuallyTrackedDdrumViews()) {
    //we are manually tracking views so we need to start tracking the view as soon as dd is initialized.
    datadogRum.startView();
  }
}

function initializeDogfood() {
  window.addEventListener('error', (event) => trackDogfoodError(event.error));
  window.addEventListener('unhandledrejection', (event) => trackDogfoodError(event.reason));
}

const webVitalsMeasureNames = {
  CLS: 'cumulative_layout_shift',
  FID: 'first_input_delay',
  INP: 'interaction_to_next_paint',
  LCP: 'largest_contentful_paint',
};

type WebVitalMetric =
  | CLSMetricWithAttribution
  | FIDMetricWithAttribution
  | INPMetricWithAttribution
  | LCPMetricWithAttribution;

function getTargetSelector(metric: WebVitalMetric): string | undefined {
  switch (metric.name) {
    case 'CLS':
      return metric.attribution.largestShiftTarget;
    case 'LCP':
      return metric.attribution.element;
    case 'FID':
    case 'INP':
      return metric.attribution.eventTarget;
    default:
      return undefined;
  }
}

function trackWebVitals(metric: WebVitalMetric) {
  const context = {
    custom: {
      measures: {
        [webVitalsMeasureNames[metric.name]]: metric.value,
      },
      attribution: metric.attribution,
    },
  };

  datadogRum.addAction(metric.name, context);

  const targetSelector = getTargetSelector(metric);
  nrAddPageAction(metric.name, {
    value: metric.value,
    rating: metric.rating,
    'url.path': window.location.pathname,
    ...(targetSelector ? { target_selector: targetSelector } : {}),
  });

  LDObserve.recordHistogram({
    name: metric.name,
    value: metric.value,
    attributes: {
      'url.path': window.location.pathname,
      rating: metric.rating,
      ...(targetSelector ? { target_selector: targetSelector } : {}),
    },
  });

  if (shouldTrackWebVitals().includes(metric.name)) {
    getDogfoodClient().track(`Gonfalon frontend ${metric.name}`, undefined, metric.value);
  }
}

function initializeWebVitals() {
  onCLS(trackWebVitals);
  onFID(trackWebVitals);
  onINP(trackWebVitals);
  onLCP(trackWebVitals);
}

/**
 * Initializes Datadog, and Catamorphic telemetry providers.
 * @param exceptions Array of exceptions that were caught before telemetry was initialized. These will be reported to the initialized providers.
 */
export function initializeTelemetry(exceptions: Error[]) {
  if (isE2ETest()) {
    return;
  }

  initializeDatadogRum();
  initializeNewRelicRum();
  initializeNewRelicGlobals();
  initializeDogfood();
  initializeWebVitals();
  initializeLDObserveGlobals();

  replayBufferedExceptions(exceptions);
}

export function replayBufferedExceptions(exceptions: Error[]) {
  // These were queued before telemetry init, so severity can't be derived from
  // context — default to medium and tag the source to keep them filterable.
  exceptions.forEach((error) => {
    const context = { severity: 'medium', owner: 'team-o11y', metadata: { source: 'pre-init-queue' } } as const;
    try {
      trackError(error, context);
    } catch {
      // An Error can pass `instanceof` and still be unusable — a frozen one with no message
      // cannot take trackError's fallback, and a proxied one can throw on any read. Report the
      // fact rather than dropping the entry, and never let it cost the exceptions behind it.
      try {
        trackError(new Error('Unreportable buffered exception'), context);
      } catch {
        // Nothing left to try.
      }
    }
  });
}

function initializeNewRelicGlobals() {
  // Mirror the Datadog global context on New Relic. No-ops when the NR agent is
  // not enabled/initialized.
  // `deployment.environment.name` mirrors the Datadog `env` dimension so
  // staging/prod traffic can be filtered apart within a single New Relic
  // application. Uses the OTel `deployment.environment.name` semantic-convention
  // key for the attribute.
  nrSetCustomAttribute('deployment.environment.name', datadogEnvironment());
  nrSetCustomAttribute('entrypoint', window.__bundle__);
  nrSetCustomAttribute('owner', DEFAULT_OWNER);
  nrSetCustomAttribute('accountId', accountId() ?? '');
  nrSetUserId(userId() ?? null);
  nrSetCustomAttribute('backendVersion', backendVersion());
  // Surfaces as `application.version`, the New Relic equivalent of the Datadog
  // `version` init dimension.
  nrSetApplicationVersion(frontendVersion() ?? null);
}

function initializeLDObserveGlobals() {
  mergeLDContextKeyAttributes({
    entrypoint: window.__bundle__,
    accountId: accountId(),
    userId: userId(),
    backendVersion: backendVersion(),
  });
}
